import { morph } from '@theme/morph';
import { Component } from '@theme/component';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * The "Choose Lenses" button.
 *
 * Its only job is to announce that the shopper wants to configure lenses. It does
 * not know what opens, or how — the configurator listens for the event and decides.
 * Keeping it that dumb means the button can live inside the buy-buttons block
 * without the two being coupled.
 *
 * @typedef {Object} RxLauncherRefs
 * @property {HTMLButtonElement} launchButton
 *
 * @extends {Component<RxLauncherRefs>}
 */
class RxLauncher extends Component {
  requiredRefs = ['launchButton'];
  #section = null;
  #generation = 0;

  connectedCallback() {
    super.connectedCallback();
    // Delegated, so the listener survives the button being morphed.
    this.addEventListener('click', this.#handleClick);
    this.#section = this.closest('.shopify-section, dialog');
    this.#section?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.#handleClick);
    this.#section?.removeEventListener(StandardEvents.productSelect, this.#handleProductSelect);
    this.#generation++;
  }

  /**
   * Follows the selected variant: variant ID, enabled state and free label
   * come from the launcher rendered in the product-select response.
   * @param {ProductSelectEvent} event
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;
    const generation = ++this.#generation;
    event.promise
      .then(({ detail }) => {
        if (!detail?.html || !this.isConnected || generation !== this.#generation) return;
        if (!detail.newProduct && detail.productId && String(detail.productId) !== this.dataset.productId) return;
        const next = detail.html.querySelector(`solstice-rx-launcher[data-product-id="${this.dataset.productId}"]`)
          ?? detail.html.querySelector('solstice-rx-launcher');
        if (!next) return;
        this.dataset.productId = next.dataset.productId ?? this.dataset.productId;
        this.dataset.variantId = next.dataset.variantId ?? this.dataset.variantId;
        morph(this, next, { childrenOnly: true });
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[solstice-rx-launcher] Event promise rejected:', error);
      });
  };

  #handleClick = (event) => {
    const button = event.target instanceof Element ? event.target.closest('button') : null;
    if (!button || button.disabled) return;
    this.dispatchEvent(
      new CustomEvent('solstice:rx:open', {
        bubbles: true,
        composed: true,
        detail: {
          productId: this.dataset.productId,
          variantId: this.dataset.variantId,
        },
      })
    );
  };
}

if (!customElements.get('solstice-rx-launcher')) {
  customElements.define('solstice-rx-launcher', RxLauncher);
}
